/**
 * React Hook: Apex Static Analysis
 *
 * Calls the ide-service POST /analyze/apex endpoint for static pattern-based
 * code quality analysis (SOQL in loops, DML in loops, hardcoded IDs, etc.).
 * Returns LintResult[] for Monaco editor squiggles (externalMarkers).
 *
 * Debounces requests by 2000ms — runs less frequently than lint.
 */

import { useState, useCallback, useRef } from "react";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { ideServiceApi, ideAuthHeaders } from "@/services/ide";
import type { LintResult } from "@ncoder/code-editor";

// ---------- API ----------

interface ToolingFinding {
  title: string;
  description?: string;
  severity: "critical" | "high" | "medium" | "low" | "info";
  class?: string;
  line?: number;
  pattern: string;
  recommendation?: string;
}

interface AnalyzeApexResponse {
  findings: ToolingFinding[];
}

const ANALYZABLE_EXTENSIONS = [".cls", ".trigger"];
const ANALYZABLE_TYPES = ["ApexClass", "ApexTrigger"];

function isAnalyzable(fileName: string, metadataType?: string): boolean {
  if (metadataType && ANALYZABLE_TYPES.includes(metadataType)) return true;
  return ANALYZABLE_EXTENSIONS.some((ext) => fileName.endsWith(ext));
}

function findingToLintResult(f: ToolingFinding): LintResult | null {
  if (!f.line) return null;

  const severity: LintResult["severity"] =
    f.severity === "critical" || f.severity === "high"
      ? "error"
      : f.severity === "medium"
        ? "warning"
        : "info";

  return {
    line: f.line,
    column: 1,
    endLine: f.line,
    endColumn: 1000,
    message: `[${f.severity.toUpperCase()}] ${f.title}${f.recommendation ? ` — ${f.recommendation}` : ""}`,
    severity,
  };
}

// ---------- Hook ----------

export function useApexAnalysis(): {
  markers: LintResult[];
  analyze: (code: string, fileName: string, metadataType?: string) => void;
} {
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id ?? null);
  const [markers, setMarkers] = useState<LintResult[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const analyze = useCallback((code: string, fileName: string, metadataType?: string) => {
    if (!isAnalyzable(fileName, metadataType)) {
      setMarkers([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const { data } = await ideServiceApi.post<AnalyzeApexResponse>(
          "/analyze/apex",
          { code, fileName },
          { signal: controller.signal, headers: ideAuthHeaders(orgId) },
        );

        const newMarkers = (data.findings || [])
          .map(findingToLintResult)
          .filter((m): m is LintResult => m !== null);

        setMarkers(newMarkers);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "CanceledError") return;
        setMarkers([]);
      }
    }, 2000);
  }, [orgId]);

  return { markers, analyze };
}
